import { useCallback, useMemo, useState } from 'react';
import type { Resolver } from 'react-hook-form';
import type { FormWizardFormStep } from './form-wizard-form-step';
import type { BaseFormWizardStepGeneric } from './form-wizard-step';

export type StepMap<TSteps extends BaseFormWizardStepGeneric, TContext extends object = {}> = Partial<{
  [K in keyof TSteps]: FormWizardFormStep<TSteps, K, TContext, Resolver<TSteps[K], TContext, TSteps[K]>>;
}>;

export type StepArray<TSteps extends BaseFormWizardStepGeneric, TContext extends object = {}> = Array<
  StepMap<TSteps, TContext>[keyof StepMap<TSteps, TContext>]
>;

export interface FormWizardStepBuilderReturnType<TSteps extends BaseFormWizardStepGeneric, TContext extends object | undefined = {}> {
  addStep: <TKey extends keyof TSteps>(
    key: TKey,
    config: Omit<FormWizardFormStep<TSteps, TKey, TContext, Resolver<TSteps[TKey], TContext, TSteps[TKey]>>, 'id'>,
  ) => void;
  updateStep: <TKey extends keyof TSteps>(
    key: TKey,
    config: Partial<Omit<FormWizardFormStep<TSteps, TKey, TContext, Resolver<TSteps[TKey], TContext, TSteps[TKey]>>, 'id'>>,
  ) => void;
  steps: FormWizardFormStep<TSteps, keyof TSteps, TContext, Resolver<TSteps[keyof TSteps], TContext, TSteps[keyof TSteps]>>[];
}

export function useFormWizardStepBuilder<TSteps extends BaseFormWizardStepGeneric, TContext extends object = {}>(
  ...initialSteps: StepArray<TSteps, TContext>
): FormWizardStepBuilderReturnType<TSteps, TContext> {
  const [insertOrder, setInsertOrder] = useState<(keyof TSteps)[]>(
    (initialSteps || []).reduce<(keyof TSteps)[]>((acc, curr) => {
      if (curr?.id) {
        acc.push(curr.id as keyof TSteps);
      }

      return acc;
    }, []),
  );
  const [steps, setSteps] = useState<StepMap<TSteps, TContext>>(
    (initialSteps || []).reduce<StepMap<TSteps, TContext>>((acc, curr) => {
      if (curr?.id) {
        acc[curr.id] = curr;
      }

      return acc;
    }, {}),
  );

  const builtSteps = useMemo(
    () =>
      insertOrder.reduce<FormWizardFormStep<TSteps, keyof TSteps, TContext, Resolver<TSteps[keyof TSteps], TContext, TSteps[keyof TSteps]>>[]>(
        (acc, key) => {
          if (steps[key]) {
            acc.push(steps[key]);
          }

          return acc;
        },
        [],
      ),
    [steps, insertOrder],
  );

  const addStep = useCallback(
    <TKey extends keyof TSteps>(
      key: TKey,
      config: Omit<FormWizardFormStep<TSteps, TKey, TContext, Resolver<TSteps[TKey], TContext, TSteps[TKey]>>, 'id'>,
    ) => {
      setSteps((prev) => ({
        ...prev,
        [key]: {
          ...config,
          id: key,
        },
      }));

      setInsertOrder((prev) => [...prev, key]);
    },
    [],
  );

  const updateStep = useCallback(
    <TKey extends keyof TSteps>(
      key: TKey,
      config: Partial<Omit<FormWizardFormStep<TSteps, TKey, TContext, Resolver<TSteps[TKey], TContext, TSteps[TKey]>>, 'id'>>,
    ) => {
      setSteps((prev) => ({
        ...prev,
        [key]: {
          ...prev[key],
          ...config,
        },
      }));
    },
    [],
  );

  return useMemo(
    () => ({
      addStep,
      updateStep,
      steps: builtSteps,
    }),
    [addStep, updateStep, builtSteps],
  );
}
